import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import ProjectModal from './ProjectModal';

import { useAppSelector } from '../../../../redux/hooks';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { ProjectType } from "../../../../types/ProjectType";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const Projects: React.FC = () => {

  const { ledgers } = useAppSelector(state => state.master);
  const [projects, setProject] = useState<ProjectType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<ProjectType | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchProject = async () => {
    try {
      const data = await apiRequest<ProjectType[]>({
        method: 'get',
        url: 'get-projects',
      });
      setProject(data);
    } catch (error) {
      showError('Failed to fetch project');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchProject();
  }, []);

  const handleEditClick = (record: ProjectType) => {
    setIsEditMode(true);
    setInitialValues({
      project_id: record.project_id,
      project_name: record.project_name,
      project_status: record.project_status,
      project_image: record.project_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchProject();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (project_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-project/${project_id}`,
      });
      showSuccess('project deleted');
      fetchProject();
    } catch {
      showError('Failed to delete project');
    }
  };

  const handleStatusToggle = async (serviceID: number) => {
    setProject((prev) =>
      prev.map((item) =>
        item.project_id === serviceID
          ? { ...item, project_status: item.project_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-project/${serviceID}`,
      });
      showSuccess('project status updated');
    } catch (error) {
      showError('Failed to update status');
      setProject((prev) =>
        prev.map((item) =>
          item.project_id === serviceID
            ? { ...item, project_status: item.project_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<ProjectType>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<ProjectType> = [

    {
      title: 'Image',
      dataIndex: 'project_image',
      key: 'project_image',
      width: 150,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.project_image}
            alt="project"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),

    },
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      render: (_, record) => <div className='text-wrap'>{record.client_name}</div>,
    },
    {
      title: 'Project Name',
      dataIndex: 'project_name',
      key: 'project_name',
      filters: Array.from(new Set(projects.map((s) => s.project_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.project_name || null,
      onFilter: (value, record) => record.project_name.includes(value as string),
      sorter: (a, b) => a.project_name.localeCompare(b.project_name),
      sortOrder: sortedInfo.columnKey === 'project_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div className='text-wrap'>{record.project_name}</div>,
    }
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 150,
      fixed: 'right',
      render: (_: any, record: ProjectType) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.project_status}
                onChange={() => handleStatusToggle(record.project_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this project?"
              onConfirm={() => handleDelete(record.project_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Project</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Project
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<ProjectType>
          tableLayout="fixed"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="project_id"
          dataSource={projects.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          // scroll={{ y: 37 * 13 }}
          scroll={{ x: 1400, y: 37 * 17 }}
          locale={{ emptyText: 'No project found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, projects.length)} of{' '}
                {projects.length}
              </span>
              <Pagination
                {...pagination}
                total={projects.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <ProjectModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        projects={projects}
        ledgers={ledgers}
      />
    </>
  );
};

export default Projects;